package com.dynamicruntime.webapp

// The antd `theme` export, aliased: inside the ConfigProvider builder block, `theme` is its prop.
import com.dynamicruntime.common.app.APP
import com.dynamicruntime.common.gedra.workflow.WAGG
import com.dynamicruntime.common.home.HMENU
import com.dynamicruntime.common.operator.OPS
import com.dynamicruntime.webapp.theme as antdTheme
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import react.FC
import react.Key
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.span
import react.useEffect
import react.useEffectOnce
import react.useState
import web.cssom.ClassName

/** Coroutine scope for the app root's suspend backend calls (the app-level config fetch). */
private val appScope = MainScope()

/**
 * The application root and top-level router. The persistent [AppBar] sits above every page; the page itself is
 * derived from the URL, through [routeParams]: `/wa/catalog` (or a deep endpoint link carrying `m=…`) shows the
 * [EndpointCatalog], the app root shows [Home], and a path no route matches the not-found page (issue #1125). It is
 * told of every move -- a link followed, a menu item, the browser's back/forward buttons -- so those switch pages.
 * The catalog's in-page navigation (table ⇄ endpoint) uses replaceState and tells nobody, so it doesn't disturb this.
 *
 * The whole tree sits inside a [ConfigProvider] on antd's dark algorithm, so antd's controls are themed to
 * match the shell's dark palette rather than rendering their light default inside it (issue #96).
 */
val App = FC<Props> {
    var page by useState(currentPage())
    // The app-wide refresh generation (issue #115): bumped on navigation and by state mutations, it re-triggers
    // every mounted config consumer. The tuple form (not `by`) is used, so the bump is a functional update
    // (`{ it + 1 }`), which the persistent route listener below needs to avoid a stale count.
    val (refresh, setRefresh) = useState(0)
    // How many times the idle poll has fired (issues #146, #1372), apart from the generation: a poll re-reads only the
    // shell's polled configs (see useIdlePolls). Tuple form for the same reason as `refresh`.
    val (polls, setPolls) = useState(0)
    // How many times the page on screen has been chosen again from the menu (issue #565). Folded into the page
    // boundary's key below for the pages in `restartOnRevisit`, so re-selecting one remounts it with fresh
    // state -- the only reset a page can get when the address does not change, since navigating to
    // the address on screen tells nobody. Tuple form for the same reason as `refresh`: the bump is a functional update.
    val (revisit, setRevisit) = useState(0)
    // What the shell knows of the caller that pages name themselves by (issue #1091), as the app bar reads it.
    val (shellFacts, setShellFacts) = useState(ShellFacts())
    // A newer web-app version detected on a response (issue #136); drives the reload affordance below. The
    // reaction is non-destructive: we never reload out from under the user, only offer it and reload on a
    // navigation (a safe point) or an explicit click.
    var updateAvailable by useState(false)
    // How often the idle bump fires (issue #146), served by the app config and re-read on every generation. Held
    // as *state* (not just the module cache), so a deployment that changes it re-arms the timer -- see useIdleBump.
    var idleBumpIntervalMs by useState(APP.defaultIdleBumpIntervalMs)
    // Whether the debug routes resolve (issue #227). Held as *state* for the same reason the interval above is:
    // the app config arrives asynchronously, and the module cache changing does not re-render anything. Reading
    // `appConfig()` straight from the router would answer "no" once, before the first fetch, and never be asked
    // again -- the debug page would simply never appear. Assigning here re-renders exactly once, when the answer
    // actually changes.
    var debugAllowed by useState(false)
    // Whether the Simulations route resolves (issue #997): only for a caller with test access, for whom simulations
    // exist (issue #1149). State for the same reason as [debugAllowed].
    var testAccess by useState(false)

    // The env-auth pair for the bar (issue #360). Held here, beside the other values derived from the app
    // config, because that config arrives asynchronously and its module cache re-renders nothing on its own.
    var envAuthSuppressible by useState(false)
    var envAuthActing by useState(false)
    var envAuthDebug by useState(false)
    // The `_debug` words the box offers (issue #1334); kept as held when a refresh tells the same list, so it redraws nothing.
    val (debugTags, setDebugTags) = useState(emptyList<DebugTagChoice>())
    // Whether to draw the testing band (issue #1150): from the app config, like the values beside it.
    var publicTestDeployment by useState(false)
    // App is the root component (it never unmounts), so the listener lives for the page's lifetime; no cleanup.
    useEffectOnce {
        onWebAppStale { updateAvailable = true }
        onRouteChangeFrom { oldUrl ->
            val next = currentPage()
            // A page holding unsaved work may veto being left (issue #700) -- asked HERE, before the switch, so a
            // "stay" changes nothing: the page never unmounts and its edits are intact. The address bar is put
            // back to exactly where it was (replaceState, which tells nobody), whatever the move was.
            if (LeaveGuard.vetoesMove(routeParams())) {
                restoreUrl(oldUrl)
                return@onRouteChangeFrom
            }
            page = next
            // Cross-page navigation is a refresh trigger: bump so every mounted config consumer re-reads.
            setRefresh { it + 1 }
            // A navigation is a safe point to pick up a newer app version if one has been detected (issue #136).
            if (isWebAppStale()) reloadWebApp()
        }
    }

    // The deployment-global app config (issue #120), fetched once at the root and re-fetched on every generation
    // like any other config, so it stays fresh. Cached module-side (see AppApi) for consumers such as the
    // error-display policy (issue #111); nothing here re-renders on it, so no state is kept.
    useEffect(refresh, polls) {
        appScope.launch {
            AppApi.load()
            // Pick up a reconfigured interval; a change re-keys useIdleBump, which retires the old timer.
            idleBumpIntervalMs = appConfig().idleBumpIntervalMs
            debugAllowed = appConfig().allowDebugPages
            testAccess = appConfig().hasTestAccess
            envAuthSuppressible = appConfig().envAuthSuppressible
            envAuthActing = appConfig().isEnvAuthed
            envAuthDebug = appConfig().envAuthDebug
            val toldTags = appConfig().debugTags
            setDebugTags { held -> if (held == toldTags) held else toldTags }
            publicTestDeployment = appConfig().publicTestDeployment
            // From the config, not the state just set: a state read in this same effect still holds the old value.
            markPageTestDeployment(appConfig().publicTestDeployment)
        }
    }

    // A periodic tick (and a bump on returning to the app) so a tab left open notices things that change
    // without any user action -- a timed-out session reverting to the anonymous menu, a newer version deployed
    // (issue #146). It counts a poll, not a generation (issue #1372): only the shell's polled configs re-read, so no
    // page re-reads its rows and no session is renewed with nobody there. A hidden tab stays silent (see useIdleBump).
    useIdleBump(idleBumpIntervalMs) { setPolls { it + 1 } }

    // antd derives its whole palette from tokens, so the dark algorithm is set once here for the whole tree.
    val darkTheme: dynamic = js("({})")
    darkTheme.algorithm = antdTheme.darkAlgorithm
    // Link colour is deliberately NOT a token here: the dark algorithm re-derives a `colorLink` seed to a dimmer
    // shade (measured: #818cf8 came out #717ad6), so it cannot pin the palette's value. `.ant-btn-link` takes its
    // colour from the app's CSS variables in app.css instead -- one source for `<a>` links and link buttons alike.
    //
    // A default (secondary) button IS themed by tokens (issue #1399): the algorithm gives it its own container colour,
    // a near-black #141414 under a #424242 hairline, darker than the slate card and panels it sits on, so it read as
    // an inset slot rather than something to press. Component tokens are taken as given, not re-derived, so these
    // pin a fill one step up the slate ramp from the card (`--border`'s #334155), a hairline above it, and a hover and
    // press that move further from the background -- as a link's does. Danger and disabled buttons keep antd's.
    darkTheme.components = js("({})")
    darkTheme.components.Button = js(
        """({
            defaultBg: '#334155', defaultBorderColor: '#475569', defaultColor: '#e2e8f0',
            defaultHoverBg: '#475569', defaultHoverBorderColor: '#64748b', defaultHoverColor: '#f8fafc',
            defaultActiveBg: '#3d4b60', defaultActiveBorderColor: '#64748b', defaultActiveColor: '#f8fafc'
        })"""
    )
    // A table is themed the same way (issue #1449): the algorithm sat it on a near-black #141414 with a #1d1d1d header
    // row, so it read as a slot cut into the card, and its header differed from every hand-built `.op-table`. These
    // put it on the card, with the header row the hand-built tables have. Read from app.css's own variables rather
    // than spelled here, so the two kinds of table cannot drift apart. The stylesheet is linked ahead of this script,
    // so they resolve; the hover and sort shades antd derives from the card follow it.
    val palette = window.getComputedStyle(document.documentElement!!)
    fun cssVar(name: String): String = palette.getPropertyValue(name).trim()
    darkTheme.components.Table = js("({})")
    darkTheme.components.Table.colorBgContainer = cssVar("--surface-raised")
    darkTheme.components.Table.headerBg = cssVar("--table-head-bg")
    darkTheme.components.Table.headerColor = cssVar("--text")
    darkTheme.components.Table.colorText = cssVar("--text")
    darkTheme.components.Table.borderColor = cssVar("--border")
    darkTheme.components.Table.headerSplitColor = cssVar("--border")

    // Before any consumer's effect runs, so a fetch that consumer makes knows which generation it is for (issue #1313).
    refreshGenerationNow = refresh
    idlePollsNow = polls
    RefreshContext.Provider {
        value = RefreshBus(refresh, revisit, polls) { setRefresh { it + 1 } }
        // What the shell knows of the caller, for the pages to name themselves by (issue #1091).
        ShellFactsContext.Provider {
            value = shellFacts
            ConfigProvider {
                theme = darkTheme

                // The backstop (issue #223). The page boundary further down is the one that normally catches, and
                // it is better: it keeps the navigation alive. This one exists for what that cannot see -- the
                // shell itself, the app bar, the banner below -- so that NOTHING renders a blank page, and so no
                // future chrome added up here has to remember to be guarded. React runs the innermost boundary
                // that matches, so adding this changes nothing about how a page failure behaves.
                //
                // It is deliberately NOT keyed: there is no navigation left to reset it on, which is exactly why
                // its fallback offers a reload instead of telling you to go elsewhere.
                ErrorBoundary {
                    fallback = ShellErrorFallback
                    onError = ::reportRenderFailure

                    // Every child here is keyed: the banners come and go, and React matches unkeyed children by their
                    // place, so a banner appearing would shift the bar and the page and remount them -- the page losing
                    // its state, an unsaved draft, or an invitation's token it had already taken from the address.
                    if (updateAvailable) {
                        div {
                            key = "updateBanner".unsafeCast<Key>()
                            className = ClassName("update-banner")
                            span { +"A new version of the app is available." }
                            button {
                                className = ClassName("update-banner-reload")
                                onClick = { reloadWebApp() }
                                +"Reload"
                            }
                        }
                    }
                    // Persistent chrome, so inside the backstop with the bar (issue #1150): fixed along the bottom
                    // edge, so it shows however far a page scrolls without competing with the sticky bar at the top.
                    if (publicTestDeployment) TestDeploymentBand { key = "testDeploymentBand".unsafeCast<Key>() }
                    AppBar {
                        key = "appBar".unsafeCast<Key>()
                        this.currentPage = menuPageOf(page)
                        this.onRevisit = { setRevisit { it + 1 } }
                        // Kept as it was when nothing changed, so a refresh that tells the same thing redraws nothing.
                        this.onShellFacts = { told -> setShellFacts { held -> if (held == told) held else told } }
                        this.envAuthSuppressible = envAuthSuppressible
                        this.envAuthActing = envAuthActing
                        this.envAuthDebug = envAuthDebug
                        this.debugTags = debugTags
                    }
                    div {
                        key = "appContent".unsafeCast<Key>()
                        className = ClassName("app-content")
                        // The boundary wraps the page, NOT the root, so a render failure costs the page and not the
                        // navigation above it -- someone (or a test) can click away from a broken screen instead of
                        // being stranded on it (issue #223).
                        //
                        // Keyed on the page for a reason that is easy to miss: React never resets a boundary on its
                        // own, so without this the fallback would survive the navigation it invites you to make, and
                        // every later page would show the earlier page's failure. The key remounts it on a page
                        // change, which is exactly when the failure stops being relevant.
                        //
                        // The debug tools are the one place `page` alone is too coarse: they all share `page=debug`
                        // and differ only by the `tool` route param (issue #517), so a faulted `tool=fault` would
                        // otherwise keep showing its fallback after a `back` to the index -- the very outliving this
                        // key exists to prevent. Folding the tool in remounts the boundary when it changes.
                        //
                        // The revisit count is folded in for the same reason from the other direction (issue #565):
                        // choosing the current page from the menu changes nothing else -- not the URL, not `page` --
                        // yet for a page that can be left waiting on something that will never come (a login code
                        // for an address with no account), it is the one gesture that says "start over". Only the
                        // pages in `restartOnRevisit` take it: a remount is not free elsewhere -- the catalog would
                        // refetch its whole listing, Profile would drop a password-change code mid-entry, and every
                        // page that registers a route listener in `useEffectOnce` would leak one per click,
                        // since `onRouteChange` has no cleanup yet (docs/development/deferred-work.md).
                        ErrorBoundary {
                            key = (
                                page + (routeParams()[debugToolParam]?.let { ":$it" } ?: "") +
                                    (if (page in restartOnRevisit) "#$revisit" else "")
                            ).unsafeCast<Key>()
                            fallback = ErrorFallback
                            onError = ::reportRenderFailure
                            when (page) {
                                pageCatalog -> EndpointCatalog {}
                                pageDocs -> DocsPage {}
                                pageLogin -> AuthFlow { mode = pageLogin }
                                pageRegister -> AuthFlow { mode = pageRegister }
                                pageInvite -> InvitePage {}
                                pageClaim -> AuthFlow { mode = pageClaim }
                                pageProfile -> Profile {}
                                pageUsers -> Users {}
                                pageEnv -> EnvReferencePage {}
                                pageOperator -> OperatorIndex {}
                                pageBootChecks -> OperatorListPage {
                                    method = "GET"
                                    path = OPS.bootChecksPath
                                    title = "Boot checks"
                                    description = "Every check this node ran at startup, its mode, and what it found."
                                }
                                pageSystemInfo -> OperatorObjectPage {
                                    path = OPS.systemInfoPath
                                    title = "System info"
                                    description = "This node's identity, uptime, and JVM statistics."
                                }
                                pageDbTables -> OperatorListPage {
                                    method = "GET"
                                    path = OPS.dbTablesPath
                                    title = "Database tables"
                                    description = "Every database table registered for this instance."
                                }
                                pageFragmentsCheck -> OperatorListPage {
                                    method = "GET"
                                    path = OPS.fragmentsCheckPath
                                    title = "Fragments check"
                                    description = "The Markdown fragment files this node carries, and any problems found."
                                }
                                pageCacheState -> OperatorCacheStatePage {}
                                pageFileStore -> OperatorFileStorePage {}
                                pageJoin -> JoinPage {}
                                pageCfacts -> CFactReferencePage {}
                                pageNewForm -> CreationPage {}
                                pageForms -> FormsPage {}
                                pageWorkflows -> WorkflowsPage {}
                                pageWorkflowForms -> FormsPage { listing = pageWorkflowForms }
                                pageClients -> ClientsPage {}
                                pageBundleEditor -> BundleEditorPage {}
                                pageCustomers -> CustomersPage {}
                                pageReports -> ReportsPage {}
                                pageApiKeys -> ApiKeysPage {}
                                pageEditForm -> EditFormPage {}
                                pageSurveyEdit -> SurveyEditPage {}
                                pageCreateForUser -> CreateForUserPage {}
                                // Resolved here rather than in `currentPage()` because the answer depends on the
                                // app config, which arrives asynchronously -- see `debugAllowed` above. Where the
                                // flag is off, this falls through to Home, so the route does not exist rather than
                                // being refused: nothing should acknowledge that a way to break the app is there
                                // (issue #227).
                                pageDebug -> if (debugAllowed) DebugPage {} else Home {}
                                // A test instance's own page (issue #997), for a caller with test access (issue
                                // #1149); elsewhere the route does not exist.
                                pageSimulations -> if (testAccess) SimulationsPage {} else Home {}
                                // Becoming someone (issue #1183), for a caller with test access, as Simulations is.
                                pageBecomeUser -> if (testAccess) BecomeUserPage {} else Home {}
                                // A path the app has no route for (issue #1125): the server answers every path with
                                // the app, so this is the one place that can say there is no such page.
                                pageNotFound -> NotFoundPage {}
                                else -> Home {}
                            }
                        }
                    }
                }
            }
        }
    }
}

private const val pageCatalog = "catalog"
// The documents page (issue #554): the listing, or one document -- and where a bare `#doc=<id>` lands.
private const val pageDocs = HMENU.pageDocs
private const val pageLogin = "login"
private const val pageRegister = "register"

// The pages that restart when their own menu item is chosen again (issue #565): the auth flow's two modes,
// whose code step can be reached for an address that will never receive one (#275 answers an unknown address
// as success on purpose), and which the address cannot reset because it does not change. Opt-in, not every page:
// see the boundary key in [App] for what a remount costs the others.
private val restartOnRevisit = setOf(pageLogin, pageRegister, HMENU.pageClaim)
// Claiming an account created for you (issue #751): the auth flow's third mode, reached from the login page.
private const val pageClaim = HMENU.pageClaim
// The invitation page (issue #751): reached from a mailed link (`/wa/invite#token=...`), never from the menu.
private const val pageInvite = HMENU.pageInvite
private const val pageProfile = "profile"
// The user-administration page. Reachable only when the shell's menu offers it (the backend decides), but the
// route exists unconditionally: the page itself reports honestly when the caller lacks the capability.
private const val pageUsers = HMENU.pageUsers

// The operator environment-variable reference (issue #371). Same route policy as Users: present
// unconditionally, offered in the menu only to operators, and the endpoint refuses a caller who reaches it
// without the role -- reported honestly by the page rather than hidden.
private const val pageEnv = HMENU.pageEnv

// The operator boot-checks diagnostic page (issue #540): present unconditionally like the other operator pages;
// the endpoint is deployment-operator-gated and the menu offer is too, so a caller without the role reaching
// the route sees the page report the refusal rather than a hidden route.
private const val pageBootChecks = HMENU.pageBootChecks

// The remaining tier-1 operator diagnostic pages (issue #540, slice 2): same route policy as boot checks --
// present unconditionally, offered in the menu only to a deployment operator, the endpoint refusing a caller
// who reaches the route without the role (reported by the page, not a hidden route).
private const val pageSystemInfo = HMENU.pageSystemInfo
private const val pageDbTables = HMENU.pageDbTables
private const val pageFragmentsCheck = HMENU.pageFragmentsCheck
private const val pageCacheState = HMENU.pageCacheState
// The operator's file-store browser (issue #1297): the same route policy; the endpoints refuse a non-operator.
private const val pageFileStore = HMENU.pageFileStore
// Joining a demo client (issue #1248): present unconditionally; the endpoints answer as absent where it is not offered.
private const val pageJoin = HMENU.pageJoin
// The Operator index/landing page (issue #540, Part B): mirrors the Debug index; reached from the Operator
// menu group's "Overview" entry. Present unconditionally; the tools it links to each refuse without the role.
private const val pageOperator = HMENU.pageOperator

// The cfact reference (issue #488). Same route policy as the environment reference: present unconditionally,
// offered in the menu only to a client-scoped operator or admin (and suppressible by a client), and the
// endpoint refuses a caller who reaches it without the role -- reported honestly by the page rather than hidden.
private const val pageCfacts = HMENU.pageCfacts

// The create-a-form page (issue #408). Login-gated on the backend (the `gedra` section), but the route exists
// unconditionally like the others -- the menu, built server-side, is what decides whether it is offered.
private const val pageNewForm = HMENU.pageNewForm

// The list/view page for the caller's form documents (issue #408); same login gating and route policy.
private const val pageForms = HMENU.pageForms

// The clients page (issue #905): offered in the menu to administrators; the route exists unconditionally, and the
// page reports honestly when the caller may not see it.
private const val pageClients = HMENU.pageClients

// The customer page (issue #1114): offered in the menu to a deployment administrator and to a customer's root
// administrators; the route exists unconditionally, and the page reports honestly when the caller may not see it.
private const val pageCustomers = HMENU.pageCustomers

// The reports page (issue #1007): offered in the menu to administrators, as Clients is; the page says so honestly
// when the caller may not run reports.
private const val pageReports = HMENU.pageReports
private const val pageApiKeys = HMENU.pageApiKeys

// The workflow pages (issue #792): the menu, built server-side, decides whether they are offered (a client turns
// the item on); the route exists unconditionally, like the others. A workflow's own listing of forms
// (`pageWorkflowForms`, defined with the forms page that draws it) is a child of it, reached from its counts.
private const val pageWorkflows = HMENU.pageWorkflows

/**
 * The menu item a page sits under, for the bar's highlight: a workflow's listing is the Workflows item's, since it
 * is that page drilled into; every other page is its own item's (or none, for a child route the menu never names).
 */
private fun menuPageOf(page: String): String = if (page == pageWorkflowForms) pageWorkflows else page
// The edit-a-form page (issue #417); its id (`pageEditForm`) lives with the page in EditFormPage.kt, since the
// view's Edit button names the same route. Reached from the view rather than the top nav; the section gates it.

// The debug area (issue #227). Present in the router unconditionally; whether it *renders* is gated on the
// deployment's `allowDebugPages`, checked at render time where the config is known.
private const val pageDebug = HMENU.pageDebug
private const val pageSimulations = HMENU.pageSimulations
private const val pageBecomeUser = HMENU.pageBecomeUser

/**
 * Resolves the page from the address's params: `page=catalog` (or an endpoint deep-link carrying `m=`) shows the
 * catalog, `page=login`/`page=register` the auth flow, `page=profile` the profile page, a path no route matches the
 * not-found page, anything else home.
 */
private fun currentPage(): String {
    val params = routeParams()
    return when {
        params[HP.page] == pageCatalog || params.containsKey(HP.method) -> pageCatalog
        params[HP.page] == pageDocs || params.containsKey(HP.doc) -> pageDocs
        params[HP.page] == pageLogin -> pageLogin
        params[HP.page] == pageRegister -> pageRegister
        params[HP.page] == pageInvite -> pageInvite
        params[HP.page] == pageClaim -> pageClaim
        params[HP.page] == pageProfile -> pageProfile
        params[HP.page] == pageUsers -> pageUsers
        params[HP.page] == pageEnv -> pageEnv
        params[HP.page] == pageBootChecks -> pageBootChecks
        params[HP.page] == pageSystemInfo -> pageSystemInfo
        params[HP.page] == pageDbTables -> pageDbTables
        params[HP.page] == pageFragmentsCheck -> pageFragmentsCheck
        params[HP.page] == pageCacheState -> pageCacheState
        params[HP.page] == pageFileStore -> pageFileStore
        params[HP.page] == pageJoin -> pageJoin
        params[HP.page] == pageOperator -> pageOperator
        params[HP.page] == pageCfacts -> pageCfacts
        params[HP.page] == pageNewForm -> pageNewForm
        // A forms address naming a workflow is the workflow's listing (issue #792) -- what a count linked to before the
        // listing had its own page, so a saved link still opens where it did; the page then rewrites the address to its own id.
        params[HP.page] == pageWorkflowForms || (params[HP.page] == pageForms && params.containsKey(WAGG.workflowId)) -> pageWorkflowForms
        params[HP.page] == pageForms -> pageForms
        params[HP.page] == pageWorkflows -> pageWorkflows
        params[HP.page] == pageClients -> pageClients
        params[HP.page] == pageBundleEditor -> pageBundleEditor
        params[HP.page] == pageCustomers -> pageCustomers
        params[HP.page] == pageReports -> pageReports
        params[HP.page] == pageApiKeys -> pageApiKeys
        params[HP.page] == pageEditForm -> pageEditForm
        params[HP.page] == pageSurveyEdit -> pageSurveyEdit
        params[HP.page] == pageCreateForUser -> pageCreateForUser
        params[HP.page] == pageDebug -> pageDebug
        params[HP.page] == pageSimulations -> pageSimulations
        params[HP.page] == pageBecomeUser -> pageBecomeUser
        params[HP.page] == pageNotFound -> pageNotFound
        else -> "home"
    }
}
